import { PaymentPlanResource, PaymentTiming, PromotionResource } from "@/interfaces";
import { calendarDayOf } from "./currency";

/**
 * The date a plan takes its first payment on.
 *
 * A plan carries its installment dates one by one — the schedule stopped being
 * implied by a start date and an interval — so the first of those dates is the
 * plan's own start.
 */
export const planFirstPaymentDate = (
  plan?: PaymentPlanResource | null
): string | null => plan?.attributes?.installment_dates?.[0] ?? null;

/**
 * Whether a plan would pay too late to earn a promotion that enforces its
 * deadline.
 *
 * False means the plan's own schedule already collects on or before the
 * deadline, so the discount is earned either way — there is nothing to ask the
 * customer, and nothing they could forfeit by keeping that schedule. Mirrors
 * ResolvePromotionPaymentDateAction::planMissesDeadline() on the API side.
 *
 * The plan's date is a plain calendar day, but stop_at is an ISO instant, so
 * the two cannot be compared as written. The deadline is resolved to the
 * customer's own calendar day first — the same day formatDate puts on screen,
 * via the same helper. Slicing the instant's leading characters instead would
 * compare against the API's frame: post-cutover a 23:59:59 Eastern deadline
 * serialises as 2026-10-17T03:59:59Z, and the portal would grant the discount
 * to a plan starting the 17th while telling the customer the offer ended on
 * the 16th (CORE-1931).
 */
export const planMissesPromotionDeadline = (
  promotion?: PromotionResource | null,
  planStartDate?: string | null
): boolean => {
  const deadline = promotion?.attributes?.stop_at;

  if (!promotion?.attributes?.requires_payment_before_stop_at || !deadline) {
    return false;
  }

  // No start date means the plan charges immediately, so it never misses.
  if (!planStartDate) {
    return false;
  }

  const deadlineDay = calendarDayOf(deadline);

  // An unreadable deadline cannot be shown to be missed.
  if (!deadlineDay) {
    return false;
  }

  return calendarDayOf(planStartDate) > deadlineDay;
};

/**
 * A deadline-bearing discount is only given up when the plan it is kept on
 * would actually pay after the deadline.
 */
export const forfeitsPromotionDiscount = (
  promotion: PromotionResource | null | undefined,
  planStartDate: string | null | undefined,
  paymentTiming: PaymentTiming
): boolean =>
  paymentTiming === "plan_schedule" && planMissesPromotionDeadline(promotion, planStartDate);
